import { ref, onValue, onDisconnect, set, serverTimestamp, push } from 'firebase/database';
import { rtdb } from './firebase';

export interface PresenceData {
  uid: string;
  path: string;
  device: string;
  lastSeen: any;
  status: 'online' | 'offline';
}

/**
 * Initializes real-time presence tracking for the current user/session.
 */
export const initPresence = (path: string) => {
  if (!rtdb) return;

  const sessionId = localStorage.getItem('sw_session') || 'sess_' + Math.random().toString(36).substring(2, 11);
  localStorage.setItem('sw_session', sessionId);

  const userStatusRef = ref(rtdb, `/status/${sessionId}`);
  const connectedRef = ref(rtdb, '.info/connected');

  const deviceType = /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop';

  onValue(connectedRef, (snapshot) => {
    if (snapshot.val() === false) return;

    // When we disconnect, remove this device
    onDisconnect(userStatusRef).remove().catch(console.error);

    // When we are connected, set the status
    set(userStatusRef, {
      id: sessionId,
      status: 'online',
      lastSeen: serverTimestamp(),
      path: path,
      device: deviceType,
      userAgent: navigator.userAgent
    });

    // Increment total views (optional, but requested in requirements)
    const viewsRef = ref(rtdb, '/analytics/totalViews');
    set(viewsRef, serverTimestamp()); // Simple way to trigger a "hit" or use a cloud function for increments
  });
};

/**
 * Subscribes to real-time status updates of all online users.
 */
export const subscribeToOnlineUsers = (callback: (users: any[]) => void) => {
  if (!rtdb) return () => {};
  const statusRef = ref(rtdb, '/status');

  return onValue(statusRef, (snapshot) => {
    const data = snapshot.val();
    const users = data ? Object.values(data) : [];
    callback(users);
  });
};

/**
 * Track unique visitors (stored in RTDB under /analytics/unique)
 */
export const trackUniqueVisitor = () => {
  if (!rtdb) return;
  const visited = localStorage.getItem('sw_visited');
  if (!visited) {
    const uniqueRef = ref(rtdb, '/analytics/uniqueVisitors');
    // Using a push to create a unique entry
    push(uniqueRef, {
      timestamp: serverTimestamp(),
      device: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop'
    });
    localStorage.setItem('sw_visited', 'true');
  }
};
